import { useEffect, useState } from "react";
import { fetchEmails, type GraphEmail } from "../services/graph";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logModuleFailure, logModuleSuccess } from "../../../telemetry";

interface UseLiveEmailsResult {
  emails: GraphEmail[];
  loading: boolean;
  error: string | null;
}

export function useLiveEmails(token: string | null, top = 25): UseLiveEmailsResult {
  const { refreshGraphToken } = useAuthState();
  const [emails, setEmails] = useState<GraphEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setEmails([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchEmails(token, top, refreshGraphToken)
      .then((result) => {
        if (cancelled) return;
        setEmails(result);
        logModuleSuccess("email", "load-emails");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load emails";
        setError(msg);
        logModuleFailure("email", e, "load-emails");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, top, refreshGraphToken]);

  return { emails, loading, error };
}
